import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config/api';

export function useArticles() {
  const { token } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/articles`);
      if (!res.ok) throw new Error('Failed to fetch articles');
      setArticles(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const getArticle = useCallback(async (id) => {
    const res = await fetch(`${API_BASE}/api/articles/${id}`);
    if (!res.ok) throw new Error('Article not found');
    return res.json();
  }, []);

  const createArticle = useCallback(async (data) => {
    const res = await fetch(`${API_BASE}/api/articles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create article');
    const article = await res.json();
    setArticles(prev => [article, ...prev]);
    return article;
  }, [token]);

  const updateArticle = useCallback(async (id, data) => {
    const res = await fetch(`${API_BASE}/api/articles/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update article');
    const article = await res.json();
    setArticles(prev => prev.map(a => a.id === id ? article : a));
    return article;
  }, [token]);

  const deleteArticle = useCallback(async (id) => {
    const res = await fetch(`${API_BASE}/api/articles/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to delete article');
    setArticles(prev => prev.filter(a => a.id !== id));
  }, [token]);

  return { articles, loading, error, fetchArticles, getArticle, createArticle, updateArticle, deleteArticle };
}
